import { Eye } from 'lucide-react';
import type { ReactNode } from 'react';
import { Logo } from '../../components/ui/logo';
import { CurrencySelector } from '../../components/CurrencySelector';
import { Separator } from '../../components/ui/separator';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import { useAuth } from '../../hooks/useAuth';
import { readGuestScope } from '../../lib/guest-token';

/**
 * Chrome for a signed-in guest: brand, a "Guest access" marker, currency, theme
 * — and no navigation, so an external approver gets their offer and nothing else.
 *
 * `html` has `overflow: hidden` app-wide, so the document never scrolls; the
 * shell owns a scroll region, the same way PlatformLayout does.
 */
export function GuestShell({ children }: { children: ReactNode }) {
	/* Read off the token's `guestPreview` claim rather than a per-tab marker, so
	   the banner is there in every tab the preview session reaches. Without it a
	   preview and a real guest session look identical — the guest screens behave
	   the same on purpose — and the first refused mutation reads as a bug rather
	   than as the point. */
	const { token } = useAuth();
	const preview = readGuestScope(token)?.preview === true;

	return (
		<div className="flex h-full flex-col overflow-hidden bg-canvas">
			{preview && (
				<div className="flex shrink-0 items-center justify-center gap-2 bg-status-warn/10 px-4 py-1.5 text-center text-xs text-status-warn">
					<Eye size={12} className="shrink-0" />
					<span>
						<span className="font-medium">Previewing as a guest.</span> This is the real
						guest view of a real quote — nothing you do here is saved, and the client
						never sees it.
					</span>
				</div>
			)}
			<header className="shrink-0 border-b border-line-1 bg-surface-sunken">
				<div className="mx-auto flex h-[65px] max-w-[var(--content-max)] items-center gap-3 px-4 sm:px-6 md:px-8">
					<Logo />
					<span className="text-2xs rounded-full border border-line-1 px-2 py-0.5 font-medium tracking-[0.05em] text-text-4 uppercase">
						Guest access
					</span>
					<div className="ml-auto flex items-center gap-3">
						<CurrencySelector />
						<Separator orientation="vertical" className="h-4 bg-line-1" />
						<ThemeToggle />
					</div>
				</div>
			</header>
			<div className="scroll-thin flex-1 overflow-auto">{children}</div>
		</div>
	);
}
